import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Plus, 
    Settings,
    Trash2,
    Edit2,
    AlertTriangle,
    RotateCcw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATALOGOS = [
    { key: 'colores_tela', label: 'Colores de Tela' },
    { key: 'colores_hilo', label: 'Colores de Hilo' },
    { key: 'tipos_tela', label: 'Tipos de Tela' },
    { key: 'tipos_avios', label: 'Tipos de Avíos' },
    { key: 'marcas', label: 'Marcas' },
    { key: 'modelos', label: 'Modelos' },
    { key: 'procesos_maquila', label: 'Procesos de Maquila' },
    { key: 'areas', label: 'Áreas de Trabajo' },
    { key: 'puestos', label: 'Puestos' },
    { key: 'medidas_cierres', label: 'Medidas de Cierres' },
    { key: 'formas_pago', label: 'Formas de Pago' },
    { key: 'tipos_pantalon', label: 'Tipos de Pantalón' },
    { key: 'colores_playera', label: 'Colores de Playera' },
    { key: 'tallas_playera', label: 'Tallas de Playera' },
    { key: 'estampados', label: 'Estampados' }
];

export const Catalogos = () => {
    const [activeTab, setActiveTab] = useState('colores_tela');
    const [items, setItems] = useState({});
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [resetStep, setResetStep] = useState(0); // 0=hidden, 1=first confirm, 2=password
    const [resetPassword, setResetPassword] = useState('');
    const [resetting, setResetting] = useState(false);
    const [form, setForm] = useState({ nombre: '', descripcion: '' });

    useEffect(() => {
        fetchAllCatalogos();
    }, []);

    const fetchAllCatalogos = async () => {
        try {
            const results = {};
            for (const cat of CATALOGOS) {
                try {
                    const response = await axios.get(`${API_URL}/api/catalogos/${cat.key}`);
                    results[cat.key] = response.data;
                } catch {
                    results[cat.key] = [];
                }
            }
            setItems(results);
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCatalogo = async (tipo) => {
        try {
            const response = await axios.get(`${API_URL}/api/catalogos/${tipo}`);
            setItems(prev => ({ ...prev, [tipo]: response.data }));
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.nombre) {
            toast.error('El nombre es requerido');
            return;
        }

        try {
            if (editingItem) {
                await axios.put(`${API_URL}/api/catalogos/${activeTab}/${editingItem.id}`, form);
                toast.success('Item actualizado');
            } else {
                await axios.post(`${API_URL}/api/catalogos/${activeTab}`, form);
                toast.success('Item agregado');
            }
            setModalOpen(false);
            setForm({ nombre: '', descripcion: '' });
            setEditingItem(null);
            fetchCatalogo(activeTab);
        } catch (error) {
            toast.error('Error al guardar');
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setForm({ nombre: item.nombre, descripcion: item.descripcion || '' });
        setModalOpen(true);
    };

    const handleDelete = async (item) => {
        if (!window.confirm('¿Eliminar este item?')) return;
        
        try {
            await axios.delete(`${API_URL}/api/catalogos/${activeTab}/${item.id}`);
            toast.success('Item eliminado');
            fetchCatalogo(activeTab);
        } catch (error) {
            toast.error('Error al eliminar');
        }
    };

    const handleReset = async () => {
        if (!resetPassword) { toast.error('Ingrese su contrasena'); return; }
        setResetting(true);
        try {
            await axios.post(`${API_URL}/api/sistema/reiniciar`, {
                password: resetPassword,
                confirmacion: 'REINICIAR'
            });
            toast.success('Sistema reiniciado correctamente');
            setResetStep(0);
            setResetPassword('');
            setTimeout(() => window.location.href = '/login', 1500);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al reiniciar');
        } finally { setResetting(false); }
    };

    const getCatalogoLabel = (key) => {
        const cat = CATALOGOS.find(c => c.key === key);
        return cat ? cat.label : key;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="catalogos-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100" >
                        Configuración de Catálogos
                    </h1>
                    <p className="text-zinc-400 mt-1">Administre las opciones disponibles en los formularios</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Sidebar with categories */}
                <Card className="card lg:col-span-1">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold text-zinc-400">CATEGORÍAS</CardTitle>
                    </CardHeader>
                    <CardContent className="p-2">
                        <div className="space-y-1">
                            {CATALOGOS.map((cat) => (
                                <button
                                    key={cat.key}
                                    onClick={() => setActiveTab(cat.key)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                        activeTab === cat.key 
                                            ? 'bg-amber-400/10 text-amber-400 font-medium' 
                                            : 'text-zinc-400 hover:bg-zinc-800'
                                    }`}
                                    data-testid={`tab-${cat.key}`}
                                >
                                    {cat.label}
                                    <span className="float-right text-xs bg-zinc-700 px-2 py-0.5 rounded-full">
                                        {items[cat.key]?.length || 0}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Content */}
                <Card className="card lg:col-span-3">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-lg font-semibold" >
                            {getCatalogoLabel(activeTab)}
                        </CardTitle>
                        <Button
                            onClick={() => {
                                setEditingItem(null);
                                setForm({ nombre: '', descripcion: '' });
                                setModalOpen(true);
                            }}
                            className="bg-amber-400 hover:bg-amber-500 text-zinc-900"
                            data-testid="add-item-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Agregar
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {items[activeTab]?.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {items[activeTab].map((item) => (
                                    <div
                                        key={item.id}
                                        className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg group"
                                        data-testid={`item-${item.id}`}
                                    >
                                        <div>
                                            <p className="font-medium text-zinc-100">{item.nombre}</p>
                                            {item.descripcion && (
                                                <p className="text-xs text-zinc-400">{item.descripcion}</p>
                                            )}
                                        </div>
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleEdit(item)}
                                            >
                                                <Edit2 className="w-4 h-4 text-amber-400" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDelete(item)}
                                            >
                                                <Trash2 className="w-4 h-4 text-red-400" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 text-zinc-400">
                                <Settings className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p>No hay elementos en este catálogo</p>
                                <p className="text-sm">Haga clic en "Agregar" para crear el primero</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="sm:max-w-[400px]" data-testid="catalogo-modal">
                    <DialogHeader>
                        <DialogTitle >
                            {editingItem ? 'Editar' : 'Agregar'} {getCatalogoLabel(activeTab)}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="form-group">
                            <Label className="form-label">Nombre *</Label>
                            <Input
                                value={form.nombre}
                                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                                placeholder="Nombre del item"
                                data-testid="input-nombre"
                            />
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Descripción (opcional)</Label>
                            <Input
                                value={form.descripcion}
                                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                                placeholder="Descripción"
                                data-testid="input-descripcion"
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="save-item-btn">
                                {editingItem ? 'Actualizar' : 'Agregar'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* ══════════ ZONA DE REINICIO ══════════ */}
            <Card className="card border-red-500/20 mt-8" data-testid="reset-section">
                <CardContent className="p-5">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-bold text-red-400 flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5" /> Zona de Peligro
                            </h3>
                            <p className="text-sm text-zinc-400 mt-1">Reiniciar el sistema borra TODOS los datos y empieza de cero. Solo se conserva tu usuario admin.</p>
                        </div>
                        {resetStep === 0 && (
                            <Button onClick={() => setResetStep(1)} variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 shrink-0" data-testid="btn-reset-start">
                                <RotateCcw className="w-4 h-4 mr-2" /> Reiniciar Sistema
                            </Button>
                        )}
                    </div>

                    {resetStep === 1 && (
                        <div className="mt-4 p-4 rounded-lg bg-red-500/5 border border-red-500/30 space-y-3" data-testid="reset-step1">
                            <p className="text-sm text-red-300 font-medium">¿Esta seguro? Se borraran TODOS los datos:</p>
                            <ul className="text-xs text-zinc-400 list-disc pl-5 space-y-1">
                                <li>Lotes, Cortes, Maquila, Lavanderia, Habilitacion</li>
                                <li>Inventarios: Telas, Cierres, Playeras, Producto Terminado, Botones</li>
                                <li>Ventas, Proveedores, Cuentas por Pagar</li>
                                <li>Empleados, Asistencia, Catalogos, Reportes</li>
                                <li>Todos los usuarios excepto el admin actual</li>
                            </ul>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => setResetStep(0)} className="flex-1">Cancelar</Button>
                                <Button onClick={() => setResetStep(2)} className="flex-1 bg-red-500 hover:bg-red-600" data-testid="btn-reset-confirm1">
                                    Si, quiero reiniciar
                                </Button>
                            </div>
                        </div>
                    )}

                    {resetStep === 2 && (
                        <div className="mt-4 p-4 rounded-lg bg-red-500/10 border-2 border-red-500/50 space-y-3" data-testid="reset-step2">
                            <p className="text-sm text-red-300 font-semibold">Ultima confirmacion — Ingrese la llave maestra:</p>
                            <Input
                                type="password"
                                value={resetPassword}
                                onChange={e => setResetPassword(e.target.value)}
                                placeholder="Llave maestra"
                                className="border-red-500/30"
                                data-testid="input-reset-password"
                            />
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => { setResetStep(0); setResetPassword(''); }} className="flex-1">Cancelar</Button>
                                <Button onClick={handleReset} disabled={resetting || !resetPassword} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="btn-reset-final">
                                    <RotateCcw className={`w-4 h-4 mr-2 ${resetting ? 'animate-spin' : ''}`} />
                                    {resetting ? 'Reiniciando...' : 'REINICIAR TODO'}
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
